import { SearchResolver } from './search-resolver.interface';
import { EsriExecute } from '../../../../config/esri.module';
import { QueryService } from '../../../../shared/service/query.service';
import { SearchSourceConfigProperties } from '../../../../config/model/search-config.model';
import { CommonUtil } from '../../../../bottom-panel/utilities/common-util';
import { AttributeType } from '../../../../shared/enum/attribute-type.enum';
import { LayerContainerService } from '../../../../shared/service/layer-container.service';
import { LayerCode } from '../../../../shared/enum/layer-code.enum';
import SuggestResult = __esri.SuggestResult;
import { TranslateService } from '@ngx-translate/core';
import {AppInjector} from "../../../../core/service/app-injector.service";

export class CustomSearchResolver implements SearchResolver {
  private readonly layerContainerService: LayerContainerService;
  private readonly queryService: QueryService;
  private readonly translateService: TranslateService;

  private readonly searchFields = [];
  private readonly orderByFields = [];
  private readonly outFields = [];
  private readonly maxResults: number = 5;
  private readonly exactMatch: boolean = false;
  private readonly suggestionTemplate: string = '';
  private readonly splitSuggestion: boolean = false;

  constructor(private readonly props: SearchSourceConfigProperties) {
    this.layerContainerService = AppInjector.getInjector().get(LayerContainerService);
    this.queryService = AppInjector.getInjector().get(QueryService);
    this.translateService = AppInjector.getInjector().get(TranslateService);

    if (!this.props.featureCode) {
      throw new Error(`Missing feature code of search configuration: ${this.props.name}`);
    }

    this.searchFields = props.searchFields ?? [];
    this.orderByFields = props.orderByFields ?? [];
    this.outFields = props.outFields ?? [];
    this.maxResults = props.maxSuggestions ?? 5;
    this.exactMatch = props.exactMatch ?? false;
    this.suggestionTemplate = props.suggestionTemplate ?? '';
    this.splitSuggestion = props.splitSuggestion;

    this.checkFields(this.searchFields, this.outFields, this.props.featureCode);
  }

  getSuggestions(params): Promise<__esri.SuggestResult[]> {
    if (!params.suggestTerm) {
      return Promise.resolve([]);
    }
    const sourceIndex = params.sourceIndex;
    const url = this.layerContainerService.getIdentifyLayerUrl(this.props.featureCode);
    const layerFields = this.getLayerFields(this.props.featureCode);
    const where = this.buildWhere(
      this.searchFields,
      layerFields,
      params.suggestTerm,
      this.exactMatch
    );
    const query = this.queryService.buildQuery(
      false,
      where,
      this.outFields.concat(['objectid']),
      this.orderByFields,
      null,
      0,
      this.maxResults
    );
    return EsriExecute.executeQueryJSON(url, query).then(result => {
      return result.features.map(x => {
        const title = this.buildTitle(x, this.outFields, this.suggestionTemplate);
        const res: SuggestResult = {
          key: x.attributes['OBJECTID'].toString(),
          text: title,
          sourceIndex: sourceIndex,
        };
        return res;
      });
    });
  }

  private buildTitle(
    feature: __esri.Graphic,
    outfields: string[],
    suggestionTemplate: string
  ): string {
    if (!suggestionTemplate) {
      const allValues = outfields.map(attr => this.getValue(attr, feature));
      return allValues.join(' / ');
    }

    let result = `${suggestionTemplate}`;

    outfields.forEach(attr => {
      const value = this.getValue(attr, feature);
      let nonZeroStartValue: string;

      if (typeof value === 'string') {
        nonZeroStartValue = value.replace(/^0+/, '');
      } else {
        nonZeroStartValue = value;
      }

      result = result.replace(`{${attr}}`, nonZeroStartValue);
      result = result.replace(
        `(${attr})`,
        this.translateService.instant(`JSON_CONFIG.SEARCH.${attr}`)
      );
    });

    return result;
  }

  private getValue(attribute: string, feature: __esri.Graphic): string {
    if (attribute.toLowerCase() === 'objectid') return;

    let value = feature.getAttribute(attribute);
    if (!CommonUtil.hasValue(value)) return '_';

    if (this.splitSuggestion) {
      const lastSpaceIndex = value.lastIndexOf(' ');
      const secondLastSpaceIndex = value.substring(0, lastSpaceIndex).lastIndexOf(' ');

      if (lastSpaceIndex === -1 || secondLastSpaceIndex === -1) {
        return value;
      }

      return (
        value.substring(0, secondLastSpaceIndex) +
        ' | ' +
        value.substring(secondLastSpaceIndex + 1, lastSpaceIndex) +
        ' | ' +
        value.substring(lastSpaceIndex, value.length)
      );
    }

    return value;
  }

  private buildWhere(
    searchFields: string[],
    layerFields: Array<any>,
    term: string,
    exactMatch: boolean
  ): string {
    const where = [];

    searchFields.forEach(field => {
      const layerField = layerFields.find(f => f.name.toLowerCase() === field.toLowerCase());
      switch (layerField?.type) {
        case AttributeType.string:
          const cleanTerm = term.replace(/[,;]/g, ' ').replace(/\s+/g, ' ');
          const trimCleanTerm = cleanTerm.trim();
          const wherePart = exactMatch
            ? `(LOWER(${field}) = '${trimCleanTerm.toLowerCase()}')`
            : `(LOWER(${field}) LIKE '%${trimCleanTerm.toLowerCase()}%')`;
          where.push(wherePart);
          break;
        case AttributeType.integer:
        case AttributeType.double:
          const value = parseInt(term);
          if (!isNaN(value)) {
            where.push(`(${field}::TEXT LIKE '%${term}%')`);
          } else {
            where.push(`1=0`);
          }
          break;
        default:
          console.warn(`Unsupported field type for search results: ${layerField} , ${layerField?.type}`);
      }
    });
    return where.join(' OR ');
  }

  private checkFields(searchFields: string[], outFields: string[], featureCode: LayerCode) {
    const layerFields = this.getLayerFields(featureCode);
    if (!layerFields || layerFields.length === 0) {
      console.log(`No fields found for the layercode: ${featureCode}`);
      return;
    }
    const supportedSerchFieldType = [
      AttributeType.string,
      AttributeType.integer,
      AttributeType.number,
      AttributeType.double,
    ];

    searchFields.forEach(field => {
      const searchField = layerFields.find(f => f.name.toLowerCase() === field.toLowerCase());
      if (!searchField) {
        throw new Error(`Missing field in layerInfos config for search: ${field}`);
      }
      if (!supportedSerchFieldType.includes(searchField.type)) {
        throw new Error(`Unsupported field type for search results: ${layerFields[field].type}`);
      }
    });

    outFields.forEach(field => {
      const searchField = layerFields.find(f => f.name.toLowerCase() === field.toLowerCase());
      if (!searchField) {
        throw new Error(`Missing field in layerInfos config for search: ${field}`);
      }
    });
  }

  private getLayerFields(featureCode: LayerCode): Array<any> {
    const layerInfoConfig = this.layerContainerService.getActiveWorkspaceLyr(featureCode)[0];
    return layerInfoConfig?.esriLayer['fields'] ?? [];
  }
}
